import React from 'react';
import { useTranslation } from 'react-i18next';

import Icon from './AppIcon';
import { useAuth } from '../contexts/AuthContext';
import { useDirection } from '../hooks/useDirection';
import { useRoleAccess } from '../hooks/useRoleAccess';
import Button from './ui/Button';

const RBACTest = () => {
  const { t } = useTranslation('common');
  const { isRTL } = useDirection();
  const { user, userProfile } = useAuth();
  const {
    userRole,
    canAccessAdmin,
    canAccessTenant,
    canManageSettings,
    canManageUsers,
    canDelete,
    canCreate,
    canUpdate,
    canRead,
    getAccessibleRoutes,
    shouldShowElement
  } = useRoleAccess();

  const testRoutes = [
    '/tenant-dashboard',
    '/tenant-maintenance',
    '/tenant-billing',
    '/tenant-profile-settings',
    '/admin-dashboard',
    '/admin-maintenance-tickets',
    '/admin-property-management',
    '/admin-lease-management',
    '/admin-invoice-management',
    '/admin-profile-settings',
    '/admin-settings-panel'
  ];

  const testElements = [
    'admin-menu',
    'settings-menu',
    'user-management',
    'tenant-menu',
    'maintenance-requests',
    'billing-info',
    'property-management',
    'lease-management',
    'invoice-management'
  ];

  return (
    <div className={`p-6 max-w-4xl mx-auto ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="card-base">
        <div className="flex items-center gap-4 mb-6">
          <Icon name="Shield" size={24} className="text-primary" />
          <h1 className="text-2xl font-bold text-card-foreground">
            {t('rbac.testTitle')}
          </h1>
        </div>

        {/* User Info */}
        <div className="bg-muted/50 rounded-lg p-4 mb-6">
          <h2 className="text-lg font-semibold text-card-foreground mb-3">
            {t('rbac.userInfo')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">{t('rbac.userId')}</p>
              <p className="font-mono text-sm">{user?.id || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('rbac.userRole')}</p>
              <p className="font-semibold text-primary">{userRole || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('rbac.userEmail')}</p>
              <p className="text-sm">{user?.email || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('rbac.profileLoaded')}</p>
              <p className="text-sm">{userProfile ? 'Yes' : 'No'}</p>
            </div>
          </div>
        </div>

        {/* Role Permissions */}
        <div className="bg-muted/50 rounded-lg p-4 mb-6">
          <h2 className="text-lg font-semibold text-card-foreground mb-3">
            {t('rbac.permissions')}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2">
              <Icon 
                name={canAccessAdmin() ? "CheckCircle" : "XCircle"} 
                size={16} 
                className={canAccessAdmin() ? "text-success" : "text-error"} 
              />
              <span className="text-sm">{t('rbac.adminAccess')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon 
                name={canAccessTenant() ? "CheckCircle" : "XCircle"} 
                size={16} 
                className={canAccessTenant() ? "text-success" : "text-error"} 
              />
              <span className="text-sm">{t('rbac.tenantAccess')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon 
                name={canManageSettings() ? "CheckCircle" : "XCircle"} 
                size={16} 
                className={canManageSettings() ? "text-success" : "text-error"} 
              />
              <span className="text-sm">{t('rbac.settingsAccess')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon 
                name={canManageUsers() ? "CheckCircle" : "XCircle"} 
                size={16} 
                className={canManageUsers() ? "text-success" : "text-error"} 
              />
              <span className="text-sm">{t('rbac.userManagement')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon 
                name={canCreate() ? "CheckCircle" : "XCircle"} 
                size={16} 
                className={canCreate() ? "text-success" : "text-error"} 
              />
              <span className="text-sm">{t('rbac.createAccess')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon 
                name={canUpdate() ? "CheckCircle" : "XCircle"} 
                size={16} 
                className={canUpdate() ? "text-success" : "text-error"} 
              />
              <span className="text-sm">{t('rbac.updateAccess')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon 
                name={canDelete() ? "CheckCircle" : "XCircle"} 
                size={16} 
                className={canDelete() ? "text-success" : "text-error"} 
              />
              <span className="text-sm">{t('rbac.deleteAccess')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon 
                name={canRead() ? "CheckCircle" : "XCircle"} 
                size={16} 
                className={canRead() ? "text-success" : "text-error"} 
              />
              <span className="text-sm">{t('rbac.readAccess')}</span>
            </div>
          </div>
        </div>

        {/* Accessible Routes */}
        <div className="bg-muted/50 rounded-lg p-4 mb-6">
          <h2 className="text-lg font-semibold text-card-foreground mb-3">
            {t('rbac.accessibleRoutes')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {testRoutes.map(route => {
              const isAccessible = getAccessibleRoutes().includes(route);
              return (
                <div key={route} className="flex items-center gap-2">
                  <Icon 
                    name={isAccessible ? "CheckCircle" : "XCircle"} 
                    size={14} 
                    className={isAccessible ? "text-success" : "text-error"} 
                  />
                  <span className="text-sm font-mono">{route}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* UI Elements Visibility */}
        <div className="bg-muted/50 rounded-lg p-4 mb-6">
          <h2 className="text-lg font-semibold text-card-foreground mb-3">
            {t('rbac.uiElements')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {testElements.map(element => {
              const shouldShow = shouldShowElement(element);
              return (
                <div key={element} className="flex items-center gap-2">
                  <Icon 
                    name={shouldShow ? "Eye" : "EyeOff"} 
                    size={14} 
                    className={shouldShow ? "text-success" : "text-error"} 
                  />
                  <span className="text-sm">{element}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Test Actions */}
        <div className="flex flex-wrap gap-4">
          <Button
            onClick={() => window.location.reload()}
            variant="outline"
            iconName="RefreshCw"
            iconPosition={isRTL ? "right" : "left"}
          >
            {t('actions.refresh')}
          </Button>
          
          <Button
            onClick={() => console.log('RBAC State:', {
              userRole,
              canAccessAdmin: canAccessAdmin(),
              canAccessTenant: canAccessTenant(),
              accessibleRoutes: getAccessibleRoutes()
            })}
            variant="outline"
            iconName="Terminal"
            iconPosition={isRTL ? "right" : "left"}
          >
            {t('rbac.logState')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RBACTest;
